import { Capacitor } from '@capacitor/core';
import { analyticsNotice } from './notice';
import { googleAnalytics } from './config';
import {FirebaseAnalytics} from '@capacitor-firebase/analytics';
import {createNativeTransport} from './nativeTransport';

const CHOICE_KEY = 'stirup_analytics_choice_v1';
const MAX_AGE = 180 * 86400000;
const screens = ['login','registration','supplier_registration','dashboard','search','catalog','cart','checkout','checkout_return','profile','orders','other'];
export function logicalScreen(pathname) {
  if (/^\/(admin|react-admin|fornitore)(\/|$)/.test(pathname)) return null;
  if (/^\/(register|supplier-register)$/.test(pathname)) return pathname === '/register' ? 'registration' : 'supplier_registration';
  if (/^\/(login)?$/.test(pathname)) return 'login';
  if (/^\/servizi\//.test(pathname)) return 'catalog';
  if (/^\/ricercaservizi/.test(pathname)) return 'search';
  if (/^\/checkout\//.test(pathname)) return 'checkout_return';
  if (/^\/profilo/.test(pathname)) return 'profile';
  if (/^\/ordini/.test(pathname)) return 'orders';
  if (/^\/dashboard/.test(pathname)) return 'dashboard';
  return null; // Password reset, payment receipt and unknown URLs are not measured.
}
const names = new Set(['page_view','registration_started','registration_result','service_search_completed',
  'supplier_catalog_viewed','add_to_cart_attempt','address_blocked','address_flow_result','add_to_cart',
  'view_cart','begin_checkout','checkout_blocked','payment_initiation_result','checkout_returned','purchase_confirmed']);
const enums = {
  screen: screens,
  category: ['lavaggio','stiratura','entrambi'],
  outcome: ['success','failure','cancelled','processing','not_completed'],
  error_code: ['network','request_rejected','address_required','incompatible_address','checkout_constraint','insufficient_balance','date_limit','unavailable'],
  gateway: ['stripe','paypal','wallet'],
  account_type: ['customer','supplier'],
};
// No arbitrary strings, URLs, form values, error messages or API objects.
export function safeParameters(input = {}) {
  const out = {};
  for (const [key, values] of Object.entries(enums)) if (values.includes(input[key])) out[key] = input[key];
  for (const key of ['result_count','quantity']) if (Number.isSafeInteger(input[key]) && input[key] >= 0 && input[key] <= 100000) out[key] = input[key];
  if (Number.isSafeInteger(Number(input.catalog_id)) && Number(input.catalog_id) > 0) out.catalog_id = String(Number(input.catalog_id));
  if (Number.isFinite(input.value) && input.value >= 0 && input.value < 1000000) out.value = Math.round(input.value * 100) / 100;
  if (input.currency === 'EUR') out.currency = 'EUR';
  return out;
}

export function createAnalytics({ config, transport, storage, now = Date.now, platform = 'web', notice = analyticsNotice }) {
  let choice = 'unset', expiresAt = 0, ready = false, lastRoute = null, generation = 0, expiryTimer;
  const seen = new Set(), listeners = new Set();
  try {
    const saved = JSON.parse(storage.getItem(CHOICE_KEY));
    if (saved?.version === notice.version && now() >= saved.at && now() - saved.at < MAX_AGE && ['granted','denied'].includes(saved.choice)) { choice = saved.choice; expiresAt=saved.at+MAX_AGE; }
  } catch {} // Storage failure always starts without permission.
  const configured = () => platform!=='web'
    ? (['android','ios'].includes(platform) && config.environment==='production' && config.nativeAvailable===true)
    : ['production','staging'].includes(config.environment)
    && /^G-[A-Z0-9]+$/.test(config.measurementId || '')
    && (config.environment === 'production' ? config.origin === 'https://app.stirup.it' : config.origin !== 'https://app.stirup.it')
    && (config.environment !== 'staging' || (config.stagingMeasurementId && config.measurementId === config.stagingMeasurementId && config.measurementId !== config.productionMeasurementId))
    ; // Native staging is off until separate Firebase test configurations exist.
  const allowed = () => notice.approved && choice === 'granted' && now()<expiresAt && configured();
  // Native SDK state persists across launches: reconcile it before React emits.
  if (platform!=='web' && !allowed()) transport.stop();
  const notify = () => listeners.forEach(fn => fn());
  function stop() { clearTimeout(expiryTimer); ready = false; generation++; seen.clear(); lastRoute = null; transport.stop(); }
  function armExpiry() {
    clearTimeout(expiryTimer);
    expiryTimer=setTimeout(()=>{
      if(now()>=expiresAt) {stop();choice='unset';try{storage.removeItem(CHOICE_KEY);}catch{}notify();}
      else armExpiry();
    },Math.min(Math.max(1,expiresAt-now()),2147483647));
    expiryTimer?.unref?.();
  }
  function getState() { return {choice:now()>=expiresAt?'unset':choice, approved:notice.approved, configured:!!configured(), platform, version:notice.version}; }
  function setConsent(next) {
    if (!['granted','denied'].includes(next) || (next === 'granted' && !notice.approved)) return false;
    choice = next;
    expiresAt=now()+MAX_AGE;
    try { storage.setItem(CHOICE_KEY,JSON.stringify({choice,version:notice.version,at:now()})); }
    catch { choice = 'denied'; stop(); notify(); return false; }
    if (choice !== 'granted') stop();
    else if(ready)armExpiry();
    notify(); return true;
  }
  function resetIdentity() {
    // Do not join an anonymous visit and another account through an analytics ID.
    stop(); choice = 'unset'; expiresAt=0;
    try { storage.removeItem(CHOICE_KEY); } catch {}
    notify();
  }
  function refreshConsent() {
    stop(); choice='unset'; expiresAt=0;
    try {
      const saved=JSON.parse(storage.getItem(CHOICE_KEY));
      if (saved?.version===notice.version && now()>=saved.at && now()-saved.at<MAX_AGE && ['granted','denied'].includes(saved.choice)) {
        choice=saved.choice; expiresAt=saved.at+MAX_AGE;
      }
    } catch {}
    notify();
  }
  function track(name, input = {}, onceKey) {
    try {
    if (!names.has(name) || !allowed()) return false;
    const params = safeParameters(input);
    if (name === 'registration_result' && !['success','failure'].includes(params.outcome)) return false;
    if (name === 'purchase_confirmed') return false; // Only confirmedPurchase accepts a backend receipt.
    if (onceKey && seen.has(`${name}:${onceKey}`)) return false;
    if (!ready) { transport.start({...config,cookieTtlSeconds:Math.max(1,Math.floor((expiresAt-now())/1000))}); ready = true; armExpiry(); }
    if (onceKey) seen.add(`${name}:${onceKey}`);
    const emitted = name === 'registration_result' && params.outcome === 'success' ? 'sign_up' : name;
    const payload = {...params,platform,environment:config.environment};
    if (emitted === 'sign_up') payload.method = 'form';
    if (['add_to_cart','view_cart','begin_checkout'].includes(name)) {
      payload.items = input.items?.map(item => ({item_id:Number.isSafeInteger(Number(item.id_servizio)) && Number(item.id_servizio)>0 ? String(Number(item.id_servizio)) : undefined,
        quantity:Number.isSafeInteger(item.qty) && item.qty>0 ? item.qty : 1})).filter(item=>item.item_id).slice(0,100) || [];
      if (params.catalog_id) payload.items = [{item_id:params.catalog_id,quantity:params.quantity || 1}];
    }
    transport.event(emitted,payload); return true;
    } catch { return false; }
  }
  function route(pathname) {
    const screen = logicalScreen(pathname);
    if (!screen) { lastRoute=null; return false; }
    if (!allowed() || pathname === lastRoute) return false;
    if (track('page_view',{screen})) { lastRoute = pathname; return true; }
    return false;
  }
  async function confirmedPurchase(reference, {token,fetch:request,apiBase}) {
    if (!allowed() || !token || typeof reference !== 'string' || !reference || reference.length>255) return false;
    const started = generation;
    try {
      const response = await request(`${apiBase}/api/analytics/purchase`,{method:'POST',cache:'no-store',
        headers:{Authorization:`Bearer ${token}`,'Content-Type':'application/json'},body:JSON.stringify({reference})});
      if (!response.ok) return false;
      const receipt = await response.json();
      if (started !== generation || !allowed() || receipt.status !== 'confirmed'
        || !/^[a-f0-9]{64}$/.test(receipt.transaction_id || '') || receipt.currency !== 'EUR'
        || !Number.isFinite(receipt.value) || receipt.value<0 || receipt.value>=1000000) return false;
      const key = `stirup_analytics_purchase:${receipt.transaction_id}`;
      const saved=Number(storage.getItem(key));
      if (seen.has(key) || (saved>0 && now()-saved<MAX_AGE)) return false;
      if (!ready) { transport.start({...config,cookieTtlSeconds:Math.max(1,Math.floor((expiresAt-now())/1000))}); ready = true; armExpiry(); }
      const params = safeParameters(receipt);
      // The opaque order reference is returned by the owner-scoped backend, never taken from the URL as proof.
      transport.event('purchase',{...params,platform,environment:config.environment,transaction_id:receipt.transaction_id,
        items:Array.isArray(receipt.items)?receipt.items.filter(i=>/^\d+$/.test(i.item_id)&&Number.isSafeInteger(i.quantity)&&i.quantity>0).map(i=>({item_id:i.item_id,quantity:i.quantity})).slice(0,100):[]});
      seen.add(key); storage.setItem(key,String(now())); return true;
    } catch { return false; } // Analytics cannot affect payment, navigation or user-facing errors.
  }
  return {getState,setConsent,resetIdentity,refreshConsent,track,route,confirmedPurchase,subscribe:fn=>{listeners.add(fn);return()=>listeners.delete(fn);}};
}

export function createWebTransport(win, doc, measurementId) {
  let script, id=measurementId, command;
  const cookiePrefix='stirup_app';
  const disabled = () => { if (id) win[`ga-disable-${id}`] = true; };
  const cleanCookies = () => {
    const suffix=/^G-[A-Z0-9]+$/.test(id || '')?id.slice(2):null;
    const ownedNames=new Set([`${cookiePrefix}_ga`,...(suffix?[`${cookiePrefix}_ga_${suffix}`,`_ga_${suffix}`]:[])]);
    for (const cookie of doc.cookie.split(';')) {
      const name = cookie.split('=')[0].trim();
      if (!ownedNames.has(name)) continue;
      // Delete only this host's app cookies, never a parent-domain cookie.
      // Include the exact stream session name for tags using the standard name.
      for (const domain of ['',`; domain=${win.location.hostname}`]) doc.cookie = `${name}=; Max-Age=0; path=/${domain}; SameSite=Lax`;
    }
  };
  return {
    start(config) {
      id = config.measurementId; win[`ga-disable-${id}`] = false;
      win.dataLayer = win.dataLayer || [];
      command = function(){ win.dataLayer.push(arguments); };
      win.gtag = command;
      command('consent','default',{analytics_storage:'denied',ad_storage:'denied',ad_user_data:'denied',ad_personalization:'denied'});
      command('consent','update',{analytics_storage:'granted',ad_storage:'denied',ad_user_data:'denied',ad_personalization:'denied'});
      command('js',new Date());
      command('config',id,{send_page_view:false,allow_google_signals:false,allow_ad_personalization_signals:false,
        page_location:'https://app.stirup.it/analytics/other',page_title:'StirUp',page_referrer:'',
        cookie_domain:win.location.hostname,cookie_prefix:cookiePrefix,cookie_path:'/',
        cookie_flags:'SameSite=Lax;Secure',cookie_expires:config.cookieTtlSeconds || 180*86400});
      if (!doc.querySelector('script[data-stirup-analytics]')) {
        script = doc.createElement('script'); script.async = true; script.dataset.stirupAnalytics = 'true';
        script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
        script.onerror = () => { disabled(); console.warn('[analytics] web tracker unavailable'); }; doc.head.appendChild(script);
      }
    },
    event(name,params) {
      if (!command || win[`ga-disable-${id}`]) return;
      const screen = params.screen || 'other';
      const location = `https://app.stirup.it/analytics/${screen}`;
      command('set',{page_location:location,page_referrer:'',page_title:screen});
      command('event',name,{...params,send_to:id,page_location:location,page_referrer:'',page_title:screen});
    },
    stop() {
      disabled();
      // Basic mode: disable first, discard unsent commands, remove identifiers.
      if (win.dataLayer) win.dataLayer.length = 0;
      script?.remove(); script = null; command = null; cleanCookies();
    },
  };
}
const storage = {getItem:key=>localStorage.getItem(key),setItem:(key,value)=>localStorage.setItem(key,value),removeItem:key=>localStorage.removeItem(key)};
const webMeasurementId=process.env.REACT_APP_ANALYTICS_ENV==='staging'?process.env.REACT_APP_GA4_STAGING_MEASUREMENT_ID:(process.env.REACT_APP_GA4_MEASUREMENT_ID || googleAnalytics.measurementId);
export const analytics = createAnalytics({
  config:{environment:process.env.REACT_APP_ANALYTICS_ENV,measurementId:webMeasurementId,
    productionMeasurementId:process.env.REACT_APP_GA4_MEASUREMENT_ID || googleAnalytics.measurementId,
    nativeAvailable:Capacitor.isPluginAvailable?.('FirebaseAnalytics')===true,
    stagingMeasurementId:process.env.REACT_APP_GA4_STAGING_MEASUREMENT_ID,origin:window.location.origin},
  platform:Capacitor.isNativePlatform()?(Capacitor.getPlatform?.() || 'native'):'web',
  transport:Capacitor.isNativePlatform()?createNativeTransport(FirebaseAnalytics):createWebTransport(window,document,webMeasurementId),storage,
});
export const track = (...args) => { try { return analytics.track(...args); } catch { return false; } };
export const confirmPurchase = reference => {
  try { return analytics.confirmedPurchase(reference,{token:localStorage.getItem('token'),fetch:window.fetch.bind(window),apiBase:process.env.REACT_APP_API_BASE_URL || ''}); }
  catch { return Promise.resolve(false); }
};
